import { createElement, isValidElement } from 'react';

import { Edge, Node, TileNode } from '@/components/diagrams/FlowChart';
import { GraphVizNodeComponentType } from '@/modules/graphViz/graphVizNodeConfig';

import { VisualizationGroupContents } from './VisualizationGroupContents';

export interface VisualizationGroup {
  id: string;
  namespace?: string | null;
  displayName: string;
  description?: string | null;
  nodeIds: string[];
  hideGroupContents?: boolean;
  componentType?: GraphVizNodeComponentType;
}

export interface GroupDiff {
  createGroups: Array<{
    tempId: string;
    displayName: string;
    description?: string | null;
    nodeIds: string[];
    namespace?: string | null;
  }>;
  updateGroups: Array<{
    id: string;
    displayName: string;
    description?: string | null;
    nodeIds: string[];
  }>;
  deleteGroupIds: string[];
}

export const TEMP_GROUP_ID_PREFIX = 'temp-group-';

export function createTemporaryGroupId(): string {
  return `${TEMP_GROUP_ID_PREFIX}${crypto.randomUUID()}`;
}

/** Update linked groups by their old name so a rename preserves their IDs and layout. */
export function applyGroupToOtherSections(
  groups: VisualizationGroup[],
  previousGroup: VisualizationGroup,
  updatedGroup: VisualizationGroup,
  nodesBySection: Record<string, Node[]>
): VisualizationGroup[] {
  const result = [...groups];
  for (const [namespace, nodes] of Object.entries(nodesBySection)) {
    if (namespace === updatedGroup.namespace) continue;
    const available = new Set(
      nodes
        .filter((node) => node.type === 'tile' && !node.data.isGroupNode)
        .map((node) => node.id.split(':').slice(1).join(':'))
    );
    const nodeIds = updatedGroup.nodeIds.filter((id) => available.has(id));
    const index = previousGroup.displayName
      ? result.findIndex(
          (group) =>
            group.namespace === namespace &&
            group.displayName === previousGroup.displayName
        )
      : -1;
    if (index >= 0) {
      result[index] = {
        ...updatedGroup,
        id: result[index]!.id,
        namespace,
        nodeIds,
      };
    } else if (nodeIds.length >= 2) {
      result.push({
        ...updatedGroup,
        id: createTemporaryGroupId(),
        namespace,
        nodeIds,
      });
    }
  }
  return normalizeGroupMembership(result);
}

export function normalizeGroupMembership(
  groups: VisualizationGroup[]
): VisualizationGroup[] {
  const claimed = new Set<string>();
  return [...groups]
    .reverse()
    .map((group) => ({
      ...group,
      nodeIds: group.nodeIds.filter((id) => {
        const key = `${group.namespace ?? ''}:${id}`;
        if (claimed.has(key)) return false;
        claimed.add(key);
        return true;
      }),
    }))
    .filter((group) => group.nodeIds.length >= 2)
    .reverse();
}

export function diffVisualizationGroups(
  original: VisualizationGroup[],
  current: VisualizationGroup[]
): GroupDiff {
  const originals = new Map(original.map((group) => [group.id, group]));
  const currentIds = new Set(current.map((group) => group.id));

  return {
    createGroups: current
      .filter((group) => group.id.startsWith(TEMP_GROUP_ID_PREFIX))
      .map(({ id, displayName, description, nodeIds, namespace }) => ({
        tempId: id,
        displayName,
        description,
        nodeIds,
        namespace,
      })),
    updateGroups: current
      .filter((group) => {
        const before = originals.get(group.id);
        return before && JSON.stringify(before) !== JSON.stringify(group);
      })
      .map(({ id, displayName, description, nodeIds }) => ({
        id,
        displayName,
        description,
        nodeIds,
      })),
    deleteGroupIds: original
      .filter((group) => !currentIds.has(group.id))
      .map((group) => group.id),
  };
}

function groupNodeId(group: VisualizationGroup, sectioned: boolean): string {
  return sectioned && group.namespace
    ? `${group.namespace}:${group.id}`
    : group.id;
}

function memberNodeId(
  group: VisualizationGroup,
  memberId: string,
  sectioned: boolean
): string {
  return sectioned && group.namespace
    ? `${group.namespace}:${memberId}`
    : memberId;
}

export function projectVisualizationGroups({
  nodes,
  edges,
  groups,
  sectioned,
  hideEntitiesListInGroups = false,
  minimumVisibleMembers = 2,
  mergeEdges,
}: {
  nodes: Node[];
  edges: Edge[];
  groups: VisualizationGroup[];
  sectioned: boolean;
  hideEntitiesListInGroups?: boolean;
  /** Keep membership validation separate from the number of visible members. */
  minimumVisibleMembers?: 1 | 2;
  mergeEdges?: (previous: Edge, current: Edge) => Edge;
}): { nodes: Node[]; edges: Edge[] } {
  const existingGroupNodes = new Map(
    nodes
      .filter((node) => node.data.isGroupNode)
      .map((node) => [node.data.visualizationGroupId, node])
  );
  const ordinaryNodes: Node[] = nodes
    .filter((node) => !node.data.isGroupNode)
    .map((node) =>
      node.data.hiddenByVisualizationGroup
        ? {
            ...node,
            hidden: !!node.data.hiddenBeforeVisualizationGroup,
            data: {
              ...node.data,
              hiddenBeforeVisualizationGroup: undefined,
              hiddenByVisualizationGroup: false,
            },
          }
        : node
    ) as Node[];
  const nodesById = new Map(ordinaryNodes.map((node) => [node.id, node]));
  const replacement = new Map<string, string>();
  const groupNodes: TileNode[] = [];

  for (const group of normalizeGroupMembership(groups)) {
    const memberIds = group.nodeIds
      .map((id) => memberNodeId(group, id, sectioned))
      .filter((id) => nodesById.get(id)?.type === 'tile');
    if (memberIds.length < minimumVisibleMembers) continue;
    const id = groupNodeId(group, sectioned);
    const first = nodesById.get(memberIds[0]!) as TileNode;
    const existing = existingGroupNodes.get(group.id) as TileNode | undefined;
    const hidden = hideEntitiesListInGroups || !!group.hideGroupContents;
    const previousContents = existing?.data.children;
    const resetHeight =
      existing &&
      isValidElement<{ hidden: boolean }>(previousContents) &&
      previousContents.props.hidden !== hidden;
    memberIds.forEach((memberId) => replacement.set(memberId, id));
    groupNodes.push({
      ...existing,
      // As on waterfall, keep the width and position but measure the new contents.
      ...(resetHeight && {
        height: undefined,
        measured: { width: existing.measured?.width, height: undefined },
      }),
      id,
      type: 'tile',
      position: existing?.position ?? first.position,
      data: {
        ...existing?.data,
        ...(resetHeight && { height: undefined }),
        lineOne: group.displayName,
        lineTwo: undefined,
        lineThree: undefined,
        variant: first.data.variant,
        sectionLabelId: group.namespace ?? first.data.sectionLabelId,
        isGroupNode: true,
        visualizationGroupId: group.id,
        componentType: group.componentType ?? existing?.data.componentType,
        notes: group.description ?? undefined,
        hideGroupContents: group.hideGroupContents ?? false,
        children: createElement(VisualizationGroupContents, {
          members: memberIds.map((memberId) => nodesById.get(memberId)!),
          hidden,
        }),
      },
    });
  }

  const projectedNodes = ordinaryNodes.map(
    (node): Node =>
      replacement.has(node.id)
        ? ({
            ...node,
            hidden: true,
            selected: false,
            data: {
              ...node.data,
              hiddenBeforeVisualizationGroup:
                node.data.hiddenBeforeVisualizationGroup ?? node.hidden,
              hiddenByVisualizationGroup: true,
            },
          } as Node)
        : node
  );
  const projectedEdges = new Map<string, Edge>();
  for (const edge of edges) {
    const source = replacement.get(edge.source) ?? edge.source;
    const target = replacement.get(edge.target) ?? edge.target;
    if (source === target) continue;
    const wasRewired = source !== edge.source || target !== edge.target;
    const key = wasRewired
      ? `grouped:${source}->${target}`
      : `original:${edge.id}`;
    if (!projectedEdges.has(key)) {
      projectedEdges.set(key, {
        ...edge,
        id: wasRewired ? key : edge.id,
        source,
        target,
      });
    } else {
      const previous = projectedEdges.get(key)!;
      const merged = mergeEdges
        ? mergeEdges(previous, edge)
        : { ...previous, data: { ...previous.data, hideLabel: true } };
      projectedEdges.set(key, {
        ...merged,
        id: previous.id,
        source: previous.source,
        target: previous.target,
      });
    }
  }

  return {
    nodes: [...projectedNodes, ...groupNodes],
    edges: Array.from(projectedEdges.values()),
  };
}
